import React from 'react';
import { theme } from '../../../../../common/styles/theme';
import { IconButton, Portal, Stack, SxProps, Tooltip } from '@mui/material';
import { IBackToSelectedProps } from './types';
import { targetItemsHeightList } from '../../CaptionsContainer/TargetCaptions/components/TargetCaptions';
import { ArrowLightLeftIcon } from '../../../../../../icons/ArrowLightLeftIcon';

export const BackToSelected: React.FC<IBackToSelectedProps> = ({
	isShow,
	selectResult,
	captionIndex,
}) => {
	if (!isShow) return null;

	const className = 'back-selected';

	const [top, setTop] = React.useState(0);
	const [left, setLeft] = React.useState(0);
	const [visible, setVisible] = React.useState(false);
	const [isTop, setIsTop] = React.useState(true);
	const containerRef = React.useRef<Element | null>();
	const selectedTop = React.useRef(0);

	const onClickHandler = () => {
		if (containerRef.current) {
			calcSelectedTop();
			containerRef.current.scrollTo({
				top: selectedTop.current - 100,
				behavior: 'smooth',
			})
		}
	}

	const calcSelectedTop = () => {
		if (!selectResult && captionIndex < 0) return;
		const index = selectResult
			? Math.round(selectResult.startCaptionIndex + (selectResult.endCaptionIndex - selectResult.startCaptionIndex) / 2)
			: captionIndex;
		let selCaptionTop = 0;
		for (let i = 0; i < index; i++) {
			selCaptionTop += targetItemsHeightList[i];
		}
		selectedTop.current = selCaptionTop;
		calcTopPosition();
	}

	const calcLeftPosition = () => {
		if (!selectResult && captionIndex < 0) return;
		let left = 0;
		const el = document.querySelector('.caption-item');
		if (el) {
			setTimeout(() => {
				let rectLeft;
				rectLeft = el.getBoundingClientRect();
				if (rectLeft) {
					left = rectLeft.left - 2;
				}
				setLeft(left);
			});
		}
		calcSelectedTop();
	};

	const calcTopPosition = () => {
		const contRect = containerRef.current?.getBoundingClientRect();
		const offset = selectedTop.current - (containerRef.current?.scrollTop || 0);

		let topPos;
		if (contRect) {
			if (offset < 0) {
				topPos = contRect.top + 20;
				setVisible(true);
				setIsTop(true);
			} else if (offset + 50 > contRect.height) {
				topPos = contRect.top + contRect.height - 20;
				setVisible(true);
				setIsTop(false);
			} else {
				topPos = contRect.top + offset + 46;
				setVisible(false);
			}
		}
		setTop(topPos);
	};

	React.useEffect(() => {
		if (!selectResult && captionIndex < 0) return;

		const resizeObserver = new ResizeObserver(calcLeftPosition);
		containerRef.current = document.querySelector('.captions-parent');
		
		if (containerRef.current) {
			containerRef.current.addEventListener('scroll', calcTopPosition);
			resizeObserver.observe(containerRef.current);
			calcLeftPosition();
			calcTopPosition();
		}

		return () => {
			if (containerRef.current) {
				containerRef.current.removeEventListener('scroll', calcTopPosition);
				resizeObserver.disconnect();
			}
		};
	}, [selectResult]);

	return (
		<Portal container={() => document.body!}>
			<Stack
				className={`${className} ${visible ? 'visible' : ''}
					${isTop ? 'top' : ''}
				`}
				gap={theme.spacing(0.5)}
				sx={{
					position: 'absolute',
					top,
            		left,
					width: theme.spacing(4),
					transform: `translate(-100%, -50%) rotate(-90deg)`,
					zIndex: 100,
					opacity: 0,
            		pointerEvents: 'none',

					'&.visible': {
						opacity: 1,
						pointerEvents: 'all',
					},

					'&.top': {
						transform: `translate(-100%, -50%) rotate(90deg)`,
					},

					'&:before': {
						content: '""',
						position: 'absolute',
						left: theme.spacing(-2),
						right: theme.spacing(-2),
						top: theme.spacing(-2),
						bottom: theme.spacing(-2),
					},
				}}
			>
				<Tooltip
					title={'Back to selected'}
					placement={'right'}
				>
					<IconButton
						onClick={onClickHandler}
						color={'primary'}
						sx={{
							flex: `0 0 ${theme.spacing(4)}`,
							width: theme.spacing(4),
							p: 0,
							color: theme.palette.common.white,
							borderRadius: '50%',
							background: theme.palette.grey[500],
						
							'.MuiSvgIcon-root': {
								width: theme.spacing(3),
								height: theme.spacing(3),
							},
						
							'&:hover, &:focus': {
								background: theme.palette.grey[700],
							},
						}}
					>
						<ArrowLightLeftIcon />
					</IconButton>
				</Tooltip>

			</Stack>
		</Portal>
	);
};
